// Contains all features that controlled by legacy policies or the new typed policies
// Features controlled by polices are either legacy quick keys policy control of a feature or a feature that will always be policy controlled
// For all new development feature use the new typed policies
// For any feature that was developed as a demo, POC, or a feature on hold, will still be controlled with the quick keys policy

// The standard format of a new typed policy is as follows
// {
//    "name": "A policy name (shown with result of policy-test)",
//     "statements": [
//         {
//             "statementId": "This is my feature flag for xxx",
//             "action": [
//                 "*"
//             ],
//             "resource": [
//                 "https://mitel.io/auth/feature-flag/features/mynewfeature"  <--- this is the new format. mynewfeature was specified in the UI and is in TypedPolicyFeatureFlags below
//             ],
//             "effect": "Allow"
//         }
//     ],
//     "type": "feature-flag" <--- this is the type of the policy. This is new and it is required
// }


export const FeatureFlags = {
    ALLOW_CX_CALL_CENTRE: 'allow-cc-next-call-centre',
    ALLOW_WORKGROUPS: 'allow-workgroups', // not defined in the UI Feature Flags, obsolete
    ALLOW_MITEL_5000: 'allow-mitel-5000',
    ALLOW_DEVELOPER_API: 'allow-developer-api',
    ALLOW_GSUITE: 'allow-gsuite',
    ALLOW_OKTA_SSO: 'allow-okta-sso',
    ALLOW_OKTA_SYNC: 'allow-okta-sync',
    ALLOW_MANAGED_ACCOUNTS: 'allow-managed-accounts',
    ALLOW_SUB_PARTNER: 'allow-sub-partner',
    ALLOW_PARTNER_TEMPLATES: 'allow-partner-templates',
    ALLOW_SYSTEM_INVENTORY: 'allow-system-inventory',
    ALLOW_SYSTEM_INVENTORY_LAUNCH_CLGW: 'allow-system-inventory-launch-clgw',
    ALLOW_WORKFLOW_STUDIO: 'allow-workflowstudio-crosslaunch',
    ALLOW_MIVB_ACD_GROUPS: 'allow-mivb-acd-groups',
    ALLOW_ALL_GROUP_MGMT: 'allow-all-group-management',
    ALLOW_MIVB_QUEUE_MGMT: 'allow-mivb-queue-management',
    ALLOW_MSTEAMS_ADDITIONAL_PBXES: 'allow-msteams-additional-pbxes',
    ALLOW_TWILIO_SMS_NUMBERS_INTEGRATION: 'allow-twilio-sms-numbers-integration',
    ALLOW_ZAPPA_PHONE: 'allow-zappa-phone',
    ALLOW_DEV_ZAPPA_PHONE: 'allow-dev-zappa--phone',
    ALLOW_UNIFY_PHONE: 'allow-unify-phone',
    ALLOW_OSV_DIRECT_SALES: 'allow-osv-direct-sales',
    ALLOW_MANAGEMENT_PROFILES: 'allow-management-profiles'
};

// this is the list of "Typed" feature policy flags
// these values must match the value supplied in the ui and the last part of the policy resource
// the string must be url valid
export const TypedPolicyFeatureFlags = {
    NOT_SUPPORTED: '',
    ALLOW_WORKGROUPS: 'allow-workgroups',
    ALLOW_MITEL_5000: 'allow-mitel-5000',
    ALLOW_PARTNER_TEMPLATES: 'allow-partner-templates',
    ALLOW_SYSTEM_INVENTORY: 'allow-system-inventory',
    ALLOW_SYSTEM_INVENTORY_LAUNCH_CLGW: 'allow-system-inventory-launch-clgw',
    ALLOW_MIVB_ACD_GROUPS: 'allow-mivb-acd-groups',
    ALLOW_ALL_GROUP_MGMT: 'allow-all-group-management',
    ALLOW_MIVB_QUEUE_MGMT: 'allow-mivb-queue-management',
    ALLOW_MSTEAMS_ADDITIONAL_PBXES: 'allow-msteams-additional-pbxes',
    ALLOW_TWILIO_SMS_NUMBERS_INTEGRATION: 'allow-twilio-sms-numbers-integration',
    ALLOW_WORKFLOW_STUDIO: 'allow-workflowstudio-crosslaunch',
    ALLOW_ZAPPA_PHONE: 'allow-zappa-phone',
    ALLOW_DEV_ZAPPA_PHONE: 'allow-dev-zappa--phone',
    ALLOW_UNIFY_PHONE: 'allow-unify-phone',
    ALLOW_OSV_DIRECT_SALES: 'allow-osv-direct-sales',
    ALLOW_MANAGEMENT_PROFILES: 'allow-management-profiles'
}

interface IFeatureKey {
    readonly featureFlag: string;
    readonly policyKey: string; // this string should be the same string specified in the UI and the same string that is the last part of the policy resource see TypedPolicyFeatureFlags above
    readonly default: boolean; // use this for to handle legacy type behaviors of features that might not be typed policy/LD controlled
    readonly ingoreRoleChange?: boolean; //maintain the state that was determined on login
}

export const FeatureKeyMap: IFeatureKey[] = [
    {
      featureFlag: FeatureFlags.ALLOW_WORKGROUPS,
      policyKey: TypedPolicyFeatureFlags.ALLOW_WORKGROUPS,
      default: false
    },
    {
        featureFlag: FeatureFlags.ALLOW_MITEL_5000,
        policyKey: TypedPolicyFeatureFlags.ALLOW_MITEL_5000,
        default: false
    },
    {
        featureFlag: FeatureFlags.ALLOW_DEVELOPER_API,
        policyKey: TypedPolicyFeatureFlags.NOT_SUPPORTED,
        default: false,
    },
    {
        featureFlag: FeatureFlags.ALLOW_CX_CALL_CENTRE,
        policyKey: TypedPolicyFeatureFlags.NOT_SUPPORTED,
        default: false
    },
    {
        featureFlag: FeatureFlags.ALLOW_GSUITE,
        policyKey: TypedPolicyFeatureFlags.NOT_SUPPORTED,
        default: false
    },
    {
        featureFlag: FeatureFlags.ALLOW_OKTA_SSO,
        policyKey: TypedPolicyFeatureFlags.NOT_SUPPORTED,
        default: false
    },
    {
        featureFlag: FeatureFlags.ALLOW_OKTA_SYNC,
        policyKey: TypedPolicyFeatureFlags.NOT_SUPPORTED,
        default: false
    },
    {
        featureFlag: FeatureFlags.ALLOW_MANAGED_ACCOUNTS,
        policyKey: TypedPolicyFeatureFlags.NOT_SUPPORTED,
        default: false
    },
    {
        featureFlag: FeatureFlags.ALLOW_SUB_PARTNER,
        policyKey: TypedPolicyFeatureFlags.NOT_SUPPORTED,
        default: false
    },
    {
        featureFlag: FeatureFlags.ALLOW_PARTNER_TEMPLATES,
        policyKey: TypedPolicyFeatureFlags.ALLOW_PARTNER_TEMPLATES,
        default: false
    },
    {
        featureFlag: FeatureFlags.ALLOW_SYSTEM_INVENTORY,
        policyKey: TypedPolicyFeatureFlags.ALLOW_SYSTEM_INVENTORY,
        default: false
    },
    {
        featureFlag: FeatureFlags.ALLOW_SYSTEM_INVENTORY_LAUNCH_CLGW,
        policyKey: TypedPolicyFeatureFlags.ALLOW_SYSTEM_INVENTORY_LAUNCH_CLGW,
        default: false
    },
    {
        featureFlag: FeatureFlags.ALLOW_WORKFLOW_STUDIO,
        policyKey: TypedPolicyFeatureFlags.ALLOW_WORKFLOW_STUDIO,
        default: false
    },
    {
        featureFlag: FeatureFlags.ALLOW_MIVB_ACD_GROUPS,
        policyKey: TypedPolicyFeatureFlags.ALLOW_MIVB_ACD_GROUPS,
        default: false
    },
    {
        featureFlag: FeatureFlags.ALLOW_ALL_GROUP_MGMT,
        policyKey: TypedPolicyFeatureFlags.ALLOW_ALL_GROUP_MGMT,
        default: false,
    },
    {
        featureFlag: FeatureFlags.ALLOW_MIVB_QUEUE_MGMT,
        policyKey: TypedPolicyFeatureFlags.ALLOW_MIVB_QUEUE_MGMT,
        default: false
    },
    {
        featureFlag: FeatureFlags.ALLOW_MSTEAMS_ADDITIONAL_PBXES,
        policyKey: TypedPolicyFeatureFlags.ALLOW_MSTEAMS_ADDITIONAL_PBXES,
        default: false
    },
    {
        featureFlag: FeatureFlags.ALLOW_TWILIO_SMS_NUMBERS_INTEGRATION,
        policyKey: TypedPolicyFeatureFlags.ALLOW_TWILIO_SMS_NUMBERS_INTEGRATION,
        default: false
    },
    {
        featureFlag: FeatureFlags.ALLOW_ZAPPA_PHONE,
        policyKey: TypedPolicyFeatureFlags.ALLOW_ZAPPA_PHONE,
        default: false
    },
    {
        featureFlag: FeatureFlags.ALLOW_DEV_ZAPPA_PHONE,
        policyKey: TypedPolicyFeatureFlags.ALLOW_DEV_ZAPPA_PHONE,
        default: false
    },
    {
        featureFlag: FeatureFlags.ALLOW_UNIFY_PHONE,
        policyKey: TypedPolicyFeatureFlags.ALLOW_UNIFY_PHONE,
        default: false
    },
    {
        featureFlag: FeatureFlags.ALLOW_OSV_DIRECT_SALES,
        policyKey: TypedPolicyFeatureFlags.ALLOW_OSV_DIRECT_SALES,
        default: false,
        ingoreRoleChange: true
    },
    {
        featureFlag: FeatureFlags.ALLOW_MANAGEMENT_PROFILES,
        policyKey: TypedPolicyFeatureFlags.ALLOW_MANAGEMENT_PROFILES,
        default: false
    }
];

export class FeatureFlagUtil {
    static featureKeyFromPolicyKey(policyKey: string): string | undefined {
        const featureKey = FeatureKeyMap.find((feature) => feature.policyKey === policyKey);
        return featureKey ? featureKey.featureFlag : undefined;
      }
  }

export enum FeatureFlagOperation {
  ENABLE = 'enable',
  DISABLE = 'disable'
}

export const featureFlagPolicyConst = {
  policyId: 'feature-flag',
  policyName: 'CloudLink feature flags policy',
  statementId: 'allow-feature-flags-account'
}

export interface FeatureFlagItem {
  id: string;
  name: string;
  description: string;
  labels: string[];
  active: boolean;
  releasedOn: Date;
  accountIds: string[];
};

export enum FeatureFlagFilterType{
  STATUS = 0
};

export enum FeatureFlagPropertyIds {
  STATUS = 'status',
  ACTIVE = 'active',
  INACTIVE = 'inactive'
};